/**
 * RankMark — a rank number that says, by its look alone, which kind it is.
 *
 *   solid     official: the rank the item holds in a Plan of Record. One per
 *             item, never shared.
 *   outlined  not official: a session's rank or a list still being decided.
 *             May tie, written "T2".
 *
 * Below the line is grayed in either kind; the number still shows (owner
 * ruling, 2026-10-13). Use this wherever a rank appears away from the page
 * that explains it, so a number seen out of context still tells the reader
 * whether it is final.
 */

import { formatRank } from '@/lib/itemRankings';

export interface RankMarkProps {
  rank: number | null;
  /** True only for a rank held in a current Plan of Record. */
  official: boolean;
  /** Other items share this rank. Ignored when `official` — a final rank is never shared. */
  tied?: boolean;
  /** False grays the mark. Null or undefined = the list draws no line. */
  aboveTheLine?: boolean | null;
  /** Was official; a newer Plan of Record replaced it. Grayed, and read out as replaced. */
  replaced?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE: Record<NonNullable<RankMarkProps['size']>, string> = {
  sm: 'h-6 min-w-6 px-1 text-xs',
  md: 'h-8 min-w-8 px-1.5 text-sm',
};

export function RankMark({ rank, official, tied = false, aboveTheLine, replaced = false, size = 'md', className = '' }: RankMarkProps) {
  const below = aboveTheLine === false;
  const gray = below || replaced;
  const text = formatRank(rank, !official && tied);

  const look = official
    ? gray
      ? 'bg-[var(--hover-lift)] text-[var(--text-ter)]'
      : 'bg-[var(--text-pri)] text-[var(--bg)]'
    : gray
      ? 'border border-dashed border-[var(--rule)] text-[var(--text-dim)]'
      : 'border border-[var(--rule-bright)] text-[var(--text-sec)]';

  const spoken = [
    rank == null ? 'No rank' : `Rank ${rank}`,
    !official && tied ? 'tied' : null,
    replaced ? 'replaced by a newer Plan of Record' : official ? 'of record' : 'not of record',
    below ? 'below the line' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <span
      aria-label={spoken}
      title={spoken}
      className={`inline-flex flex-shrink-0 items-center justify-center font-mono tabular-nums ${SIZE[size]} ${look} ${className}`}
    >
      {text}
    </span>
  );
}
